import type { Simplify } from 'type-fest';

export enum RevisionLicenseConclusionScope {
  GLOBAL = 'global',
  ORGANIZATION = 'organization',
  PROJECT = 'project',
  REVISION = 'revision',
  RELEASE_GROUP = 'release_group',
  RELEASE = 'release',
}

export type ConclusionEvidence = {
  conclusionHash: string;
  /**
   * True when the conclusion pass ran and the revision had no licenses at all, so there was
   * nothing to conclude. This distinguishes a deliberate empty conclusion from one left behind by
   * a conclusion that was skipped or failed, which the lazy catch-up needs to retry.
   */
  noLicenses?: boolean;
} & Record<string, unknown>;

type NewBaseRevisionLicenseConclusion = {
  dependencyRevisionLocator: string | null;
  dependencyProjectLocator: string;
  concludedLicenses: string[];
};

/**
 * A conclusion that is automatically generated during builds.
 */
export type NewAutoRevisionLicenseConclusion = Simplify<NewBaseRevisionLicenseConclusion & {
  scope: null;
  justification: string;
  evidence: ConclusionEvidence;
  /** True when the Legal Agent path produced the conclusion. Defaults to false. */
  isLegalAgent?: boolean;
  /** `Legal Agent` on Legal Agent rows; absent (null) on existing-path rows. */
  lastEditedBy?: string;
}>;

/**
 * An override created by a FOSSA admin.
 */
export type NewGlobalRevisionLicenseConclusion = Simplify<NewBaseRevisionLicenseConclusion & {
  scope: RevisionLicenseConclusionScope.GLOBAL;
  lastEditedBy: string;
}>;

/**
 * An override created by a user for a specific scope.
 */
export type NewScopedRevisionLicenseConclusion = Simplify<NewBaseRevisionLicenseConclusion & {
  organizationId: number;
  scope: Exclude<RevisionLicenseConclusionScope, RevisionLicenseConclusionScope.GLOBAL>;
  scopeId: string;
  lastEditedBy: string;
}>;

export type NewRevisionLicenseConclusion =
  | NewAutoRevisionLicenseConclusion
  | NewGlobalRevisionLicenseConclusion
  | NewScopedRevisionLicenseConclusion;

export type RevisionLicenseConclusion = {
  id: number;
  dependencyRevisionLocator: string | null;
  dependencyProjectLocator: string;
  organizationId: number | null;
  scope: RevisionLicenseConclusionScope | null;
  scopeId: string | null;
  concludedLicenses: string[];
  justification: string | null;
  lastEditedBy: string | null;
  isLegalAgent: boolean;
  evidence: ConclusionEvidence | null;
  createdAt: Date;
  updatedAt: Date;
};
